import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Box, IconButton, InputAdornment, TextField } from '@mui/material'
import { CheckCircle, RadioButtonUnchecked, Search } from '@mui/icons-material'

import { useStores } from '@trivie/core'
import { QuestionApi } from '@trivie/core/src/services/api-objects/QuestionApi'

import { Dialog } from '../dialogs/dialog'
import { color } from '../../theme'
import { Text } from '../text'
import { CardApi } from '@trivie/core/src/services/api-objects/CardApi'

export interface AddCardFromLibraryProps {
  open: boolean
  onClose: (selectedQuestions: any) => void
}

export const AddCardFromLibrary = observer((props: AddCardFromLibraryProps) => {
  const { open, onClose } = props
  const { cardStore } = useStores()
  const { cards, loadCards } = cardStore
  const [availableCards, setAvailableCards] = useState<any>([])
  const [search, setSearch] = useState('')

  const handleSubmit = async () => {
    const selectedCards = availableCards
      .filter((c: any) => c.selected)
      .map((c: any) => ({
        ...c,
      }))
    const detailPromises = selectedCards.map(async (c: any, index: number) => {
      const result = await CardApi.getInfoCard(c.id)

      selectedCards[index] = {
        ...selectedCards[index],
        ...result.data,
      }
    })
    await Promise.all(detailPromises)
    onClose(selectedCards)
  }

  useEffect(() => {
    if (open) {
      setSearch('')
      loadCards().then(() => {
        setAvailableCards(cards)
      })
    }
  }, [open])

  const handleCardSelected = (card_id: number, value: boolean) => {
    const updatedCards = availableCards.map((card: any) => {
      if (card.id === card_id) {
        return { ...card, selected: value }
      } else {
        return card
      }
    })
    setAvailableCards(updatedCards)
  }

  const filteredCards =
    availableCards?.filter((q: any) =>
      q.card_name.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
    ) ?? []

  return (
    <Dialog
      title="Add Card from Library"
      aria-labelledby="add card from library dialog"
      open={open}
      onClose={() => onClose([])}
      onCancel={() => onClose([])}
      onConfirm={handleSubmit}
      disableConfirm={!availableCards.filter((q: any) => q.selected).length}
      maxWidth="md"
      confirmBtnLabel="Add Card"
    >
      <TextField
        variant="standard"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search htmlColor={color.palette.grey} />
            </InputAdornment>
          ),
        }}
        style={{ marginBottom: 32, width: '50%', paddingLeft: 10 }}
        id="standard-search"
        placeholder="Search All Cards"
        type="search"
        onChange={(e) => setSearch(e.target.value)}
        value={search}
      />
      <Box overflow="auto" height={600}>
        <div
          style={{
            display: 'flex',
            padding: '12px 16px',
            borderRadius: 10,
            backgroundColor: color.palette.paperBorder,
            width: '100%',
            marginBottom: 10,
          }}
        >
          <Text variant="body2Emphasized" text="All Cards" />
        </div>
        <div
          style={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            overflowY: 'auto',
            paddingLeft: 16,
            paddingRight: 16,
          }}
        >
          {filteredCards.map((c: any, index: number) => {
            const lastItem = index >= filteredCards.length - 1
            const b = lastItem ? '' : '1px solid'
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: b,
                  borderBottomColor: color.shade10,
                }}
              >
                <Text variant="body1Medium" text={c.card_name} style={{ flex: 1 }} />
                <IconButton onClick={() => handleCardSelected(c.id, !c.selected)} size="large">
                  {c.selected ? (
                    <CheckCircle htmlColor={color.palette.blueLink} />
                  ) : (
                    <RadioButtonUnchecked />
                  )}
                </IconButton>
              </div>
            )
          })}
        </div>
        <div
          style={{
            display: 'flex',
            placeItems: 'flex-end',
            placeContent: 'flex-end',
            marginTop: 10,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingLeft: 10,
            }}
          >
            {availableCards.filter((c: any) => c.selected).length ? (
              <Text
                variant="body1Medium"
                text={`${availableCards.filter((c: any) => c.selected).length} Card${availableCards.filter((c: any) => c.selected).length === 1 ? '' : 's'} Selected`}
                style={{ color: color.palette.grey }}
              />
            ) : (
              <div />
            )}
          </div>
        </div>
      </Box>
    </Dialog>
  )
})
